import {
  collection,
  CollectionReference,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore';
import firebaseApp from '../firebase-app';
import { FS_PATH_APP_CONFIGS } from '../constants';
import { useUserAuth } from '../contexts/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppConfigData } from '../types/app-config';
import { useStringsContext } from '../contexts/strings';
import { APP_TYPE, OptionsGroup, SUPPORTED_LEAGUES } from '../types';
import { StringType } from '../sv-strings';
import { getAppConfigPath } from '../utils/paths';
import { useQuery, useQueryClient } from '@tanstack/react-query';

function groupAppsPerLeague(strings: StringType, appConfigs: AppConfigData[]) {
  const leagues = [...Object.values(SUPPORTED_LEAGUES), 'league'];
  const appsWithLeagues: OptionsGroup[] = leagues
    .map((league) => {
      const filteredAppConfigs = appConfigs.filter((appConfig) =>
        league === 'league'
          ? appConfig.appType === APP_TYPE.LEAGUE
          : appConfig.appType === APP_TYPE.CLUB && appConfig.configLeagueName === league
      );
      return {
        name: league === 'league' ? strings.apps.tabLeague : league,
        options: filteredAppConfigs.map(({ appShortname }) => appShortname),
        optionsText: filteredAppConfigs.map(
          ({ name, appShortname }) => `${name} (${appShortname})`
        ),
      };
    })
    .filter((group) => group.options.length > 0);

  return appsWithLeagues;
}

export const useAppConfigs = () => {
  const [appConfigs, appConfigsLoading, appConfigsError] = useCollectionDataOnce<AppConfigData>(
    collection(firebaseApp.db, FS_PATH_APP_CONFIGS) as CollectionReference<AppConfigData>
  );
  const { hasTeamAccess, loadingCurrentUser } = useUserAuth();

  const filteredAppConfigs = useMemo(() => {
    if (!loadingCurrentUser && appConfigs) {
      return appConfigs?.filter((appConfig) => hasTeamAccess(appConfig.appShortname));
    }
    return null;
  }, [appConfigs, hasTeamAccess, loadingCurrentUser]);

  return {
    loading: appConfigsLoading || loadingCurrentUser,
    error: appConfigsError,
    appConfigs: filteredAppConfigs,
  };
};

export const useAppConfigOptions = () => {
  const { appConfigs, loading, error } = useAppConfigs();
  const { strings } = useStringsContext();

  const appConfigOptions = useMemo(() => {
    if (appConfigs) {
      return groupAppsPerLeague(strings, appConfigs);
    }
    return null;
  }, [appConfigs, strings]);

  return {
    loading,
    error,
    appConfigOptions,
  };
};

export const useAppConfig = (teamAbbrv: string) => {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const queryKey = useMemo(() => ['appconfig-data', teamAbbrv], [teamAbbrv]);

  const getAppConfigData = async (teamAbbrv: string): Promise<AppConfigData | null> => {
    const appConfig = await getDoc(doc(firebaseApp.db, getAppConfigPath(teamAbbrv)));
    return (appConfig?.data() as AppConfigData) || null;
  };

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const appConfigData = await getAppConfigData(teamAbbrv);
      return appConfigData;
    },
    enabled: !!teamAbbrv,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!teamAbbrv) {
      return undefined;
    }

    const appConfigRef = doc(firebaseApp.db, getAppConfigPath(teamAbbrv));
    const unsubscribe = onSnapshot(appConfigRef, (snapshot) => {
      queryClient.setQueryData<AppConfigData | null>(
        queryKey,
        snapshot.exists() ? (snapshot.data() as AppConfigData) : null
      );
    });

    return unsubscribe;
  }, [queryClient, queryKey, teamAbbrv]);

  const updateAppConfig = useCallback(
    async (patch: Partial<AppConfigData>) => {
      if (!teamAbbrv) return;

      setIsUpdating(true);
      try {
        const appConfigRef = doc(firebaseApp.db, getAppConfigPath(teamAbbrv));
        await setDoc(appConfigRef, patch, { merge: true });

        queryClient.setQueryData<AppConfigData | null>(queryKey, (prev: any) =>
          prev ? { ...prev, ...patch } : (patch as AppConfigData)
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [queryClient, queryKey, teamAbbrv]
  );

  return {
    query,
    appConfig: query.data || null,
    loading: query.isLoading,
    error: query.error,
    isUpdating,
    updateAppConfig,
  };
};
